// District tenancy (Phase 1): resolves this project's "current" school id.
//
// Until the multi-school UI exists, every project is a single school and the id
// is stored at app_settings/global.defaultSchoolId (written by
// scripts/backfill-school-id.js). New documents get stamped with it so they are
// already in scope once Phase 2 rules enforce schoolId. Pre-backfill projects
// have no defaultSchoolId yet → this returns null. Student creates must then
// fail (rules require schoolId); meetings/feedback fail only in multi-school.
//
// See Developer/DISTRICT_TENANCY_DESIGN.md.

import { doc, getDoc } from 'firebase/firestore'
import { db } from '@/firebase'
import { useSchoolStore } from '@/store/schoolStore'
import { useAuthStore } from '@/store/authStore'

export const NO_SCHOOL_FOR_CREATE =
  'Select a school before creating this record. New students, meetings, and feedback forms must belong to a school.'

/**
 * True when the session is multi-school or the JWT already carries schoolIds.
 * Those callers must not write unstamped creates — rules will deny student
 * creates without schoolId, and meetings/feedback should not silently omit it.
 */
export function sessionNeedsSchoolId({ schoolCount = 0, tokenSchoolIds = null } = {}) {
  if (Number(schoolCount) >= 2) return true
  return Array.isArray(tokenSchoolIds) && tokenSchoolIds.length > 0
}

/**
 * Return a usable school id, or throw when the write must not omit one.
 * @param {string|null|undefined} schoolId
 * @param {{ required?: boolean, sessionNeedsSchool?: boolean }} opts
 * @returns {string|null}
 */
export function assertSchoolIdForCreate(
  schoolId,
  { required = false, sessionNeedsSchool = false } = {}
) {
  const id = typeof schoolId === 'string' && schoolId.trim() ? schoolId.trim() : null
  if (id) return id
  if (required || sessionNeedsSchool) {
    throw new Error(NO_SCHOOL_FOR_CREATE)
  }
  return null
}

/**
 * How a school-scoped list query should constrain `schoolId`.
 *
 * `inScopeForList` denies the *entire* query if any matching doc is out of
 * scope or unstamped. School-tier listeners must therefore pass
 * `where('schoolId', '==', X)` once a school is known. District-wide callers
 * (district_admin / coordinator / allSchools CM / unstamped admin)
 * may list unconstrained. School-tier with no claim waits (do not fire a
 * query the tight inScopeForList will deny).
 *
 * @returns {{ mode: 'school'|'all'|'wait', schoolId: string|null }}
 */
export function listQuerySchoolScope({
  currentSchoolId = null,
  tokenSchoolIds = null,
  isDistrictWide = false
} = {}) {
  const current =
    typeof currentSchoolId === 'string' && currentSchoolId.trim() ? currentSchoolId.trim() : null
  if (current) return { mode: 'school', schoolId: current }
  if (isDistrictWide) return { mode: 'all', schoolId: null }
  const ids = Array.isArray(tokenSchoolIds)
    ? tokenSchoolIds.filter((id) => typeof id === 'string' && id.trim())
    : []
  if (ids.length === 0) return { mode: 'wait', schoolId: null }
  if (ids.length === 1) return { mode: 'school', schoolId: ids[0] }
  return { mode: 'wait', schoolId: null }
}

export function isDistrictWideListUser(user) {
  const role = user?.role || ''
  if (role === 'district_admin' || role === 'district_coordinator' || user?.allSchools === true) {
    return true
  }
  // Vendor / district `admin` with no campus stamp — same as districtWideScope.
  return role === 'admin' && !Array.isArray(user?.schoolIds)
}

let cachedSchoolId
let inFlight = null

/**
 * Resolve the school id to stamp on new writes.
 *
 * Prefers the actively-selected school (multi-site users / district admins);
 * falls back to the project default at app_settings/global.defaultSchoolId
 * (single-school projects). In a single-school project both are the same id, so
 * this is a no-op there.
 * @returns {Promise<string|null>}
 */
export async function getCurrentSchoolId() {
  // Active school first (set once the school registry is loaded).
  try {
    const schoolStore = useSchoolStore()
    if (schoolStore.currentSchoolId) return schoolStore.currentSchoolId
  } catch {
    // Pinia not active (e.g. unit tests / pre-init) → fall back to project default.
  }

  if (cachedSchoolId !== undefined) return cachedSchoolId
  if (inFlight) return inFlight

  inFlight = (async () => {
    try {
      const snap = await getDoc(doc(db, 'app_settings', 'global'))
      cachedSchoolId = snap.exists() ? snap.data().defaultSchoolId || null : null
    } catch {
      cachedSchoolId = null
    } finally {
      inFlight = null
    }
    return cachedSchoolId
  })()

  return inFlight
}

/**
 * Resolve a school id for a create write.
 * Student creates pass `{ required: true }` (Firestore requires schoolId).
 * Meetings/feedback omit `required` and only throw in a multi-school session.
 * @param {{ required?: boolean }} opts
 * @returns {Promise<string|null>}
 */
export async function requireSchoolIdForCreate({ required = false } = {}) {
  const schoolId = await getCurrentSchoolId()
  let sessionNeedsSchool = false
  try {
    const schoolStore = useSchoolStore()
    const authStore = useAuthStore()
    const activeCount = (schoolStore.allSchools || []).filter((s) => s.active !== false).length
    sessionNeedsSchool = sessionNeedsSchoolId({
      schoolCount: activeCount,
      tokenSchoolIds: authStore.currentUser?.schoolIds
    })
  } catch {
    /* Pinia not active — fall through to required/schoolId only */
  }
  return assertSchoolIdForCreate(schoolId, { required, sessionNeedsSchool })
}

/** Clear the cache (e.g. after switching schools in a future multi-site UI). */
export function resetSchoolContext() {
  cachedSchoolId = undefined
  inFlight = null
}
